<script lang="ts">
  import { themeSettingsStore, COLOR_PRESETS, type PresetName } from '../stores/themeSettingsStore';
  import { themeStore } from '../stores/themeStore';
  import { 
    IconX, 
    IconRefresh, 
    IconPalette, 
    IconTypography, 
    IconBrush, 
    IconCircleDot,
    IconAlertTriangle,
    IconColorSwatch,
    IconBell
  } from '@tabler/icons-svelte';
  import { _ } from 'svelte-i18n';
  import type { ThemeSettings } from '../stores/themeSettingsStore';
  import type { Theme } from '../stores/themeStore';
  import { notificationStore } from '../stores/notificationStore';
  import { 
    isNotificationSupported, 
    getNotificationPermission, 
    requestNotificationPermission 
  } from '../utils/notifications';
  import { isSpeechSynthesisSupported } from '../utils/speechSynthesis';
  import { onMount } from 'svelte';
  
  export let isOpen = false;
  
  type Tab = 'presets' | 'colors' | 'thresholds' | 'notifications';
  let activeTab: Tab = 'presets';
  
  let currentTheme: Theme = 'dark';
  let settings: ThemeSettings = {
    background: '#f8fafc',
    text: '#0f172a',
    accent: '#10b981',
    backgroundGradientStart: '#f8fafc',
    backgroundGradientEnd: '#f8fafc',
    textGreen: '#16a34a',
  };
  
  const presetNames: PresetName[] = ['default', 'ocean', 'sunset', 'forest'];
  const presetLabels: Record<PresetName, string> = {
    default: 'Default',
    ocean: 'Ocean',
    sunset: 'Sunset',
    forest: 'Forest',
  };
  
  // Use reactive statements for stores
  $: currentTheme = $themeStore;
  $: settings = { ...$themeSettingsStore };
  $: notificationsEnabled = $notificationStore.enabled;
  $: speechSynthesisEnabled = $notificationStore.speechSynthesisEnabled;
  $: badgesEnabled = $notificationStore.badgesEnabled;
  
  let notificationPermission: NotificationPermission = 'default';
  let isRequestingPermission = false;
  
  onMount(() => {
    if (isNotificationSupported()) {
      notificationPermission = getNotificationPermission();
    }
  });
  
  // Update permission status when it changes
  $: if (isNotificationSupported()) {
    notificationPermission = getNotificationPermission();
  }
  
  function handleColorChange(key: keyof ThemeSettings, value: string) {
    themeSettingsStore.setCustomColor(key, value);
  }
  
  function handleThresholdChange(type: 'yellow' | 'red', value: number) {
    if (type === 'yellow') {
      themeSettingsStore.setYellowThreshold(value);
    } else {
      themeSettingsStore.setRedThreshold(value);
    }
  }
  
  function handlePresetSelect(presetName: PresetName) {
    const preset = COLOR_PRESETS[presetName];
    if (preset) {
      // Ensure gradient colors are always set (use background as fallback if not provided)
      const settingsWithGradient: ThemeSettings = {
        ...preset,
        backgroundGradientStart: preset.backgroundGradientStart || preset.background,
        backgroundGradientEnd: preset.backgroundGradientEnd || preset.background,
        // Dark mode gradients
        backgroundGradientStartDark: preset.backgroundGradientStartDark || preset.backgroundDark || preset.background,
        backgroundGradientEndDark: preset.backgroundGradientEndDark || preset.backgroundDark || preset.background,
      };
      // Apply preset directly to ensure immediate update
      themeSettingsStore.setThemeSettings(settingsWithGradient);
    }
  }
  
  function handleReset() {
    themeSettingsStore.resetToDefaults();
    themeStore.reset();
    // Reset notification settings to defaults
    notificationStore.setEnabled(false);
    notificationStore.setSpeechSynthesisEnabled(false);
    notificationStore.setBadgesEnabled(true);
  }
  
  function handleClose() {
    isOpen = false;
  }
  
  function handleNotificationToggle(enabled: boolean) {
    notificationStore.setEnabled(enabled);
  }
  
  function handleSpeechSynthesisToggle(enabled: boolean) {
    notificationStore.setSpeechSynthesisEnabled(enabled);
  }
  
  function handleBadgesToggle(enabled: boolean) {
    notificationStore.setBadgesEnabled(enabled);
  }
  
  async function handleRequestPermission() {
    if (!isNotificationSupported()) return;
    
    isRequestingPermission = true;
    try {
      const permission = await requestNotificationPermission();
      notificationPermission = permission;
      if (permission === 'granted') {
        // Auto-enable notifications when permission is granted
        notificationStore.setEnabled(true);
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    } finally {
      isRequestingPermission = false;
    }
  }
  
  // Close on Escape key
  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      handleClose();
    }
  }
</script>

<svelte:window on:keydown={handleKeydown} />

{#if isOpen}
  <!-- Backdrop -->
  <div
    class="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm z-40 transition-opacity"
    on:click={handleClose}
    on:keydown={(e) => e.key === 'Escape' && handleClose()}
    role="button"
    tabindex="-1"
    aria-label="Close settings"
  ></div>
  
  <!-- Settings Modal -->
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
    role="dialog"
    aria-modal="true"
    aria-labelledby="settings-title"
  >
    <div
      class="glass-card p-6 md:p-8 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col pointer-events-auto"
      role="region"
      aria-label="Theme settings content"
    >
      <!-- Header -->
      <div class="flex items-center justify-between mb-6 flex-shrink-0">
        <h2 id="settings-title" class="text-2xl font-bold text-custom-heading dark:text-white">
          {$_(`settings.title`)}
        </h2>
        <button
          on:click={handleClose}
          class="glass-button p-2 rounded-lg hover:bg-white/30 dark:hover:bg-white/20 transition-all"
          aria-label="Close settings"
        >
          <IconX class="w-5 h-5" />
        </button>
      </div>
      
      <!-- Tab Selection -->
      <div class="mb-6 flex-shrink-0">
        <label
          for="settings-tab-select"
          class="block text-sm font-semibold text-custom-heading dark:text-white mb-2"
        >
          {$_(`settings.category`)}
        </label>
        <select
          id="settings-tab-select"
          bind:value={activeTab}
          class="glass-input w-full px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer focus:ring-2 focus:ring-accent dark:focus:ring-accent"
          aria-label="Select settings category"
        >
          <option value="presets">
            {$_(`settings.tabs.presets`)}
          </option>
          <option value="colors">
            {$_(`settings.tabs.colors`)}
          </option>
          <option value="thresholds">
            {$_(`settings.tabs.thresholds`)}
          </option>
          <option value="notifications">
            {$_(`settings.tabs.notifications`)}
          </option>
        </select>
      </div>
      
      <!-- Tab Content -->
      <div class="flex-1 overflow-y-auto min-h-0">
        <!-- Presets Tab -->
        {#if activeTab === 'presets'}
          <div>
            <div class="mb-4 p-3 rounded-lg bg-blue-500/20 dark:bg-blue-500/10 border border-blue-500/30 dark:border-blue-500/20 text-blue-800 dark:text-blue-200 text-sm">
              Presets work in both light and dark mode. Colors will automatically adapt to the current theme.
            </div>
            
            <!-- Color Presets -->
            <div class="mb-6">
              <div class="flex items-center gap-2 text-sm font-semibold text-custom-heading dark:text-white mb-3">
                <IconColorSwatch class="w-4 h-4 icon-accent" />
                Color Presets
              </div>
              <div class="grid grid-cols-2 gap-3">
                {#each presetNames as presetName}
            {@const preset = COLOR_PRESETS[presetName]}
            {@const currentSettings = $themeSettingsStore}
            {@const currentGradientStart = currentSettings.backgroundGradientStart || currentSettings.background}
            {@const currentGradientEnd = currentSettings.backgroundGradientEnd || currentSettings.background}
            {@const presetGradientStart = preset.backgroundGradientStart || preset.background}
            {@const presetGradientEnd = preset.backgroundGradientEnd || preset.background}
            {@const currentGradientStartDark = currentSettings.backgroundGradientStartDark || currentSettings.backgroundDark || currentSettings.background}
            {@const currentGradientEndDark = currentSettings.backgroundGradientEndDark || currentSettings.backgroundDark || currentSettings.background}
            {@const presetGradientStartDark = preset.backgroundGradientStartDark || preset.backgroundDark || preset.background}
            {@const presetGradientEndDark = preset.backgroundGradientEndDark || preset.backgroundDark || preset.background}
            {@const isActive = currentSettings.background === preset.background &&
              currentSettings.text === preset.text &&
              currentSettings.accent === preset.accent &&
              currentSettings.textGreen === preset.textGreen &&
              currentGradientStart === presetGradientStart &&
              currentGradientEnd === presetGradientEnd &&
              (currentSettings.backgroundDark || preset.backgroundDark ? 
                (currentSettings.backgroundDark === preset.backgroundDark &&
                 (currentSettings.textDark || '') === (preset.textDark || '') &&
                 (currentSettings.accentDark || '') === (preset.accentDark || '') &&
                 (currentSettings.textGreenDark || '') === (preset.textGreenDark || '') &&
                 currentGradientStartDark === presetGradientStartDark &&
                 currentGradientEndDark === presetGradientEndDark) : true)}
                <button
              on:click={() => handlePresetSelect(presetName)}
              class="glass-button p-3 rounded-lg transition-all text-left hover:bg-white/50 dark:hover:bg-white/20 cursor-pointer {isActive ? 'ring-2 ring-offset-2' : ''}"
              style={isActive ? (currentTheme === 'light' ? `--tw-ring-color: var(--color-accent-light) !important;` : `--tw-ring-color: var(--color-accent-dark) !important;`) : ''}
              aria-label="Apply {presetLabels[presetName]} preset"
              aria-pressed={isActive}
            >
              <!-- Gradient Preview -->
              <div
                class="w-full h-8 rounded-md mb-2 border border-slate-300 dark:border-white/20"
                style="background: linear-gradient(to bottom right, {preset.backgroundGradientStart || preset.background}, {preset.backgroundGradientEnd || preset.background})"
                aria-hidden="true"
                title="Background gradient preview"
              ></div>
              
              <div class="flex items-center gap-2">
                <div class="flex gap-1">
                  <!-- Light mode colors -->
                  <div
                    class="w-4 h-4 rounded-full border border-slate-300 dark:border-white/20"
                    style="background-color: {preset.background}"
                    aria-hidden="true"
                    title="Light mode background"
                  ></div>
                  <div
                    class="w-4 h-4 rounded-full border border-slate-300 dark:border-white/20"
                    style="background-color: {preset.accent}"
                    aria-hidden="true"
                    title="Light mode accent"
                  ></div>
                  <div
                    class="w-4 h-4 rounded-full border border-slate-300 dark:border-white/20"
                    style="background-color: {preset.textGreen}"
                    aria-hidden="true"
                    title="Light mode text green"
                  ></div>
                  <!-- Dark mode colors (if available) -->
                  {#if preset.backgroundDark}
                    <div class="w-px h-4 bg-slate-300 dark:bg-white/20 mx-0.5" aria-hidden="true"></div>
                    <div
                      class="w-4 h-4 rounded-full border border-slate-300 dark:border-white/20"
                      style="background-color: {preset.backgroundDark}"
                      aria-hidden="true"
                      title="Dark mode background"
                    ></div>
                    <div
                      class="w-4 h-4 rounded-full border border-slate-300 dark:border-white/20"
                      style="background-color: {preset.accentDark || preset.accent}"
                      aria-hidden="true"
                      title="Dark mode accent"
                    ></div>
                    <div
                      class="w-4 h-4 rounded-full border border-slate-300 dark:border-white/20"
                      style="background-color: {preset.textGreenDark || preset.textGreen}"
                      aria-hidden="true"
                      title="Dark mode text green"
                    ></div>
                  {/if}
                </div>
                <span class="text-xs font-semibold text-custom dark:text-white">
                  {presetLabels[presetName]}
                </span>
              </div>
                </button>
                {/each}
              </div>
            </div>
          </div>
        {/if}
        
        <!-- Colors Tab -->
        {#if activeTab === 'colors'}
          <div>
            <!-- Color Pickers -->
            <div class="space-y-6">
              <!-- Background Color -->
              <div>
          <label
            for="bg-color"
            class="flex items-center gap-2 text-sm font-semibold text-custom-heading dark:text-white mb-2"
          >
            <IconPalette class="w-4 h-4 icon-accent" />
            Background Color
          </label>
          <div class="flex items-center gap-3">
            <input
              id="bg-color"
              type="color"
              bind:value={settings.background}
              on:input={(e) => handleColorChange('background', e.currentTarget.value)}
              class="w-16 h-10 rounded-lg border-2 border-slate-300 dark:border-white/20 cursor-pointer"
              aria-label="Background color picker"
            />
            <input
              type="text"
              bind:value={settings.background}
              on:input={(e) => handleColorChange('background', e.currentTarget.value)}
              class="flex-1 glass-input px-3 py-2 rounded-lg text-sm"
              placeholder="#f8fafc"
              aria-label="Background color hex value"
            />
              </div>
            </div>
            
            <!-- Text Color -->
            <div>
          <label
            for="text-color"
            class="flex items-center gap-2 text-sm font-semibold text-custom-heading dark:text-white mb-2"
          >
            <IconTypography class="w-4 h-4 icon-accent" />
            Text Color
          </label>
          <div class="flex items-center gap-3">
            <input
              id="text-color"
              type="color"
              bind:value={settings.text}
              on:input={(e) => handleColorChange('text', e.currentTarget.value)}
              class="w-16 h-10 rounded-lg border-2 border-slate-300 dark:border-white/20 cursor-pointer"
              aria-label="Text color picker"
            />
            <input
              type="text"
              bind:value={settings.text}
              on:input={(e) => handleColorChange('text', e.currentTarget.value)}
              class="flex-1 glass-input px-3 py-2 rounded-lg text-sm"
              placeholder="#0f172a"
              aria-label="Text color hex value"
            />
              </div>
            </div>
            
            <!-- Accent Color -->
            <div>
          <label
            for="accent-color"
            class="flex items-center gap-2 text-sm font-semibold text-custom-heading dark:text-white mb-2"
          >
            <IconBrush class="w-4 h-4 icon-accent" />
            Accent Color (Buttons & Icons)
          </label>
          <div class="flex items-center gap-3">
            <input
              id="accent-color"
              type="color"
              bind:value={settings.accent}
              on:input={(e) => handleColorChange('accent', e.currentTarget.value)}
              class="w-16 h-10 rounded-lg border-2 border-slate-300 dark:border-white/20 cursor-pointer"
              aria-label="Accent color picker"
            />
            <input
              type="text"
              bind:value={settings.accent}
              on:input={(e) => handleColorChange('accent', e.currentTarget.value)}
              class="flex-1 glass-input px-3 py-2 rounded-lg text-sm"
              placeholder="#10b981"
              aria-label="Accent color hex value"
            />
              </div>
            </div>
            
            <!-- Background Gradient -->
            <div>
          <div class="flex items-center gap-2 text-sm font-semibold text-custom-heading dark:text-white mb-3">
            <IconPalette class="w-4 h-4 icon-accent" />
            Background Gradient
          </div>
          <div class="grid grid-cols-2 gap-4">
            <!-- Gradient Start -->
            <div>
              <label
                for="bg-gradient-start"
                class="block text-xs font-medium text-custom-secondary dark:text-white/70 mb-2"
              >
                Start Color
              </label>
              <div class="flex items-center gap-2">
                <input
                  id="bg-gradient-start"
                  type="color"
                  bind:value={settings.backgroundGradientStart}
                  on:input={(e) => handleColorChange('backgroundGradientStart', e.currentTarget.value)}
                  class="w-12 h-10 rounded-lg border-2 border-slate-300 dark:border-white/20 cursor-pointer"
                  aria-label="Background gradient start color picker"
                />
                <input
                  type="text"
                  bind:value={settings.backgroundGradientStart}
                  on:input={(e) => handleColorChange('backgroundGradientStart', e.currentTarget.value)}
                  class="flex-1 glass-input px-2 py-2 rounded-lg text-xs"
                  placeholder="#f8fafc"
                  aria-label="Background gradient start color hex value"
                />
              </div>
            </div>
            
            <!-- Gradient End -->
            <div>
              <label
                for="bg-gradient-end"
                class="block text-xs font-medium text-custom-secondary dark:text-white/70 mb-2"
              >
                End Color
              </label>
              <div class="flex items-center gap-2">
                <input
                  id="bg-gradient-end"
                  type="color"
                  bind:value={settings.backgroundGradientEnd}
                  on:input={(e) => handleColorChange('backgroundGradientEnd', e.currentTarget.value)}
                  class="w-12 h-10 rounded-lg border-2 border-slate-300 dark:border-white/20 cursor-pointer"
                  aria-label="Background gradient end color picker"
                />
                <input
                  type="text"
                  bind:value={settings.backgroundGradientEnd}
                  on:input={(e) => handleColorChange('backgroundGradientEnd', e.currentTarget.value)}
                  class="flex-1 glass-input px-2 py-2 rounded-lg text-xs"
                  placeholder="#f8fafc"
                  aria-label="Background gradient end color hex value"
                />
              </div>
            </div>
          </div>
        </div>
        
            <!-- Text Green Color -->
            <div>
          <label
            for="text-green"
            class="flex items-center gap-2 text-sm font-semibold text-custom-heading dark:text-white mb-2"
          >
            <IconCircleDot class="w-4 h-4 icon-accent" />
            Text Green Color (Cost Display & Timer)
          </label>
          <div class="flex items-center gap-3">
            <input
              id="text-green"
              type="color"
              bind:value={settings.textGreen}
              on:input={(e) => handleColorChange('textGreen', e.currentTarget.value)}
              class="w-16 h-10 rounded-lg border-2 border-slate-300 dark:border-white/20 cursor-pointer"
              aria-label="Text green color picker"
            />
            <input
              type="text"
              bind:value={settings.textGreen}
              on:input={(e) => handleColorChange('textGreen', e.currentTarget.value)}
              class="flex-1 glass-input px-3 py-2 rounded-lg text-sm"
              placeholder="#16a34a"
              aria-label="Text green color hex value"
            />
              </div>
            </div>
            </div>
          </div>
        {/if}
        
        <!-- Thresholds Tab -->
        {#if activeTab === 'thresholds'}
          <div>
            <!-- Favicon Thresholds -->
            <div>
              <h3 class="flex items-center gap-2 text-lg font-semibold text-custom-heading dark:text-white mb-4">
                <IconAlertTriangle class="w-5 h-5 icon-accent" />
                {$_(`settings.faviconThresholds.title`)}
              </h3>
              <p class="text-sm text-slate-600 dark:text-white/70 mb-4">
                {$_(`settings.faviconThresholds.description`)}
              </p>
              
              <div class="space-y-4">
                <!-- Yellow Threshold -->
                <div>
            <label
              for="yellow-threshold"
              class="block text-sm font-semibold text-slate-900 dark:text-white mb-2"
            >
              {$_(`settings.faviconThresholds.yellowThreshold`)}
            </label>
            <input
              id="yellow-threshold"
              type="number"
              min="0"
              step="1"
              bind:value={settings.yellowThreshold}
              on:input={(e) => handleThresholdChange('yellow', Number(e.currentTarget.value))}
              class="w-full glass-input px-3 py-2 rounded-lg text-sm"
              aria-label="Yellow threshold value"
            />
                </div>
                
                <!-- Red Threshold -->
                <div>
            <label
              for="red-threshold"
              class="block text-sm font-semibold text-slate-900 dark:text-white mb-2"
            >
              {$_(`settings.faviconThresholds.redThreshold`)}
            </label>
            <input
              id="red-threshold"
              type="number"
              min="0"
              step="1"
              bind:value={settings.redThreshold}
              on:input={(e) => handleThresholdChange('red', Number(e.currentTarget.value))}
              class="w-full glass-input px-3 py-2 rounded-lg text-sm"
              aria-label="Red threshold value"
            />
                </div>
              </div>
            </div>
          </div>
        {/if}
        
        <!-- Notifications Tab -->
        {#if activeTab === 'notifications'}
          <div>
            <!-- Browser Notifications -->
            <div>
              <h3 class="flex items-center gap-2 text-lg font-semibold text-custom-heading dark:text-white mb-4">
                <IconBell class="w-5 h-5 icon-accent" />
                {$_(`settings.notifications.title`)}
              </h3>
              <p class="text-sm text-slate-600 dark:text-white/70 mb-4">
                {$_(`settings.notifications.description`)}
              </p>
              
              {#if !isNotificationSupported()}
                <div class="p-3 rounded-lg bg-yellow-500/20 dark:bg-yellow-500/10 border border-yellow-500/30 dark:border-yellow-500/20 text-yellow-800 dark:text-yellow-200 text-sm">
                  {$_(`settings.notifications.notSupported`)}
                </div>
              {:else}
                <div class="space-y-4">
                  <!-- Enable Notifications Toggle -->
                  <div class="flex items-center justify-between">
              <label
                for="notifications-enabled"
                class="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white cursor-pointer"
              >
                <input
                  id="notifications-enabled"
                  type="checkbox"
                  checked={notificationsEnabled}
                  on:change={(e) => handleNotificationToggle(e.currentTarget.checked)}
                  disabled={notificationPermission !== 'granted'}
                  class="w-4 h-4 rounded border-slate-300 dark:border-white/20 text-accent focus:ring-2 focus:ring-accent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Enable notifications"
                />
                <span>{$_(`settings.notifications.enabled`)}</span>
              </label>
                  </div>
                  
                  <!-- Permission Status -->
                  <div class="space-y-2">
                    {#if notificationPermission === 'granted'}
                      <div class="p-3 rounded-lg bg-green-500/20 dark:bg-green-500/10 border border-green-500/30 dark:border-green-500/20 text-green-800 dark:text-green-200 text-sm">
                        {$_(`settings.notifications.permissionGranted`)}
                      </div>
                    {:else if notificationPermission === 'denied'}
                      <div class="p-3 rounded-lg bg-red-500/20 dark:bg-red-500/10 border border-red-500/30 dark:border-red-500/20 text-red-800 dark:text-red-200 text-sm">
                        {$_(`settings.notifications.permissionDenied`)}
                      </div>
                    {:else}
                      <div class="p-3 rounded-lg bg-blue-500/20 dark:bg-blue-500/10 border border-blue-500/30 dark:border-blue-500/20 text-blue-800 dark:text-blue-200 text-sm">
                        {$_(`settings.notifications.permissionDefault`)}
                      </div>
                    {/if}
                    
                    {#if notificationPermission !== 'granted'}
                      <button
                  on:click={handleRequestPermission}
                  disabled={isRequestingPermission || notificationPermission === 'denied'}
                  class="glass-button flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Request notification permission"
                >
                        <IconBell class="w-4 h-4" />
                        {isRequestingPermission ? 'Requesting...' : $_(`settings.notifications.requestPermission`)}
                      </button>
                    {/if}
                  </div>
                </div>
              {/if}
              
              <!-- Speech Synthesis -->
              <div class="mt-6 pt-6 border-t border-slate-200 dark:border-white/10">
                <h3 class="flex items-center gap-2 text-lg font-semibold text-custom-heading dark:text-white mb-4">
                  <IconBell class="w-5 h-5 icon-accent" />
                  {$_(`settings.notifications.speechSynthesisTitle`)}
                </h3>
                <p class="text-sm text-slate-600 dark:text-white/70 mb-4">
                  {$_(`settings.notifications.speechSynthesisDescription`)}
                </p>
                
                {#if !isSpeechSynthesisSupported()}
                  <div class="p-3 rounded-lg bg-yellow-500/20 dark:bg-yellow-500/10 border border-yellow-500/30 dark:border-yellow-500/20 text-yellow-800 dark:text-yellow-200 text-sm">
                    {$_(`settings.notifications.speechSynthesisNotSupported`)}
                  </div>
                {:else}
                  <div class="flex items-center justify-between">
                    <label
                      for="speech-synthesis-enabled"
                      class="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white cursor-pointer"
                    >
                      <input
                        id="speech-synthesis-enabled"
                        type="checkbox"
                        checked={speechSynthesisEnabled}
                        on:change={(e) => handleSpeechSynthesisToggle(e.currentTarget.checked)}
                        class="w-4 h-4 rounded border-slate-300 dark:border-white/20 text-accent focus:ring-2 focus:ring-accent cursor-pointer"
                        aria-label="Enable speech synthesis"
                      />
                      <span>{$_(`settings.notifications.speechSynthesisEnabled`)}</span>
                    </label>
                  </div>
                {/if}
              </div>
              
              <!-- Badges -->
              <div class="mt-6 pt-6 border-t border-slate-200 dark:border-white/10">
                <h3 class="flex items-center gap-2 text-lg font-semibold text-custom-heading dark:text-white mb-4">
                  <IconBell class="w-5 h-5 icon-accent" />
                  {$_(`settings.notifications.badgesTitle`)}
                </h3>
                <p class="text-sm text-slate-600 dark:text-white/70 mb-4">
                  {$_(`settings.notifications.badgesDescription`)}
                </p>
                
                <div class="flex items-center justify-between">
                  <label
                    for="badges-enabled"
                    class="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white cursor-pointer"
                  >
                    <input
                      id="badges-enabled"
                      type="checkbox"
                      checked={badgesEnabled}
                      on:change={(e) => handleBadgesToggle(e.currentTarget.checked)}
                      class="w-4 h-4 rounded border-slate-300 dark:border-white/20 text-accent focus:ring-2 focus:ring-accent cursor-pointer"
                      aria-label="Enable badges"
                    />
                    <span>{$_(`settings.notifications.badgesEnabled`)}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        {/if}
      </div>
      
      <!-- Actions -->
      <div class="mt-8 flex gap-3">
        <button
          on:click={handleReset}
          class="glass-button flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all flex-1"
          aria-label="Reset to default colors"
        >
          <IconRefresh class="w-4 h-4" />
          Reset to Defaults
        </button>
        <button
          on:click={handleClose}
          class="glass-button px-6 py-2 rounded-lg font-semibold transition-all"
          aria-label="Close settings"
        >
          Close
        </button>
      </div>
    </div>
  </div>
{/if}
